/**
 * Vocational classification field definitions (occupation category + physical
 * demand) for the patient history form.
 *
 * PROVENANCE: Ported from ara-health
 *   server/data/questionSets/universal-occupational.json
 *   (questions `occupation_category`, `heaviest_lift`, `current_work_status`)
 *   on 2026-07-11 — one-time port of the field definitions and value sets.
 *   No ongoing dependency, no shared code with ara-health.
 *
 * The value tokens and option wording are kept IDENTICAL to Ara's so that the
 * A1 shadow mapping (ClinHx -> Ara) is a deterministic direct copy:
 *   - HEAVIEST_LIFT_OPTIONS  -> Ara `heaviest_lift` (feeds Ara's vocational-
 *     determination `physical_demand`: light/moderate/heavy; `none` carries no
 *     determination class).
 *   - OCCUPATION_CATEGORY_OPTIONS -> Ara `occupation_category` (job-type;
 *     contextual — not a vocational-determination classification input).
 *   - WORK_STATUS_OPTIONS -> Ara `current_work_status` (PATIENT-reported work
 *     status, 5-state). This is the patient side of Ara's dual work-status;
 *     the referrer-reported side is extracted from the referral letter into
 *     Referral.referrerReportedWorkStatus, so a discrepancy is preserved.
 *
 * If Ara's value set changes, this is a one-time re-port decision, NOT an
 * automatic sync. See docs (ara-health): docs/architecture/intake-form-diff.md
 * and canonical-intake-data-dictionary.md.
 */

// Ara `heaviest_lift` — SINGLE_SELECT, verbatim options (value token + label).
export const HEAVIEST_LIFT_OPTIONS = [
  { value: 'none', label: 'No lifting' },
  { value: 'light', label: 'Light — under 10 kg' },
  { value: 'moderate', label: 'Moderate — 10 to 25 kg' },
  { value: 'heavy', label: 'Heavy — over 25 kg' },
];

// Ara `occupation_category` — SINGLE_SELECT, verbatim options (value token + label).
export const OCCUPATION_CATEGORY_OPTIONS = [
  { value: 'sedentary_office', label: 'Sedentary / office' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'trades_manual', label: 'Trades or manual' },
  { value: 'agricultural_outdoor', label: 'Agricultural or outdoor' },
  { value: 'transport_driving', label: 'Transport or driving' },
  { value: 'education', label: 'Education' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'retail', label: 'Retail' },
  { value: 'emergency_services', label: 'Emergency services' },
  { value: 'professional_sport', label: 'Professional sport' },
  { value: 'not_currently_working', label: 'Not currently working' },
  { value: 'other', label: 'Other' },
];

// Ara `current_work_status` — SINGLE_SELECT, 5-state, verbatim (value + label).
// Patient-reported side of the dual work-status.
export const WORK_STATUS_OPTIONS = [
  { value: 'full_duties', label: 'Full duties — back to normal' },
  { value: 'modified_duties', label: 'Modified or restricted duties' },
  { value: 'off_work_expected_return', label: 'Off work but expected to return' },
  { value: 'off_work_no_expected_return', label: 'Off work without an expected return date' },
  { value: 'not_working_pre_injury', label: 'Not working before the injury either' },
];
